import { Asteroid, AsteroidSize } from '../entities/Asteroid';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class AsteroidManager {
  private gameWidth: number;
  private gameHeight: number;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private asteroids: Asteroid[] = [];

  constructor(
    gameWidth: number,
    gameHeight: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public initializeWave(level: number): void {
    this.asteroids = [];
    
    // Number of large asteroids increases with level, but caps at reasonable amount
    const baseAsteroids = 4;
    const levelAsteroids = Math.min(2, Math.floor((level - 1) / 2)); // +1 every 2 levels, max +2
    const totalAsteroids = baseAsteroids + levelAsteroids;
    
    console.log(`Initializing wave ${level} with ${totalAsteroids} large asteroids`);
    
    for (let i = 0; i < totalAsteroids; i++) {
      this.spawnLargeAsteroid(i);
    }
  }

  private spawnLargeAsteroid(index: number): void {
    // Spawn asteroids around the edges, avoiding the center where player spawns
    const centerX = this.gameWidth / 2;
    const centerY = this.gameHeight / 2;
    const safeBoundary = 150; // Safe zone around player spawn
    
    let x: number, y: number;
    
    // Try multiple times to find a safe spawn position
    let attempts = 0;
    do {
      const side = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
      
      switch (side) {
        case 0: // Top edge
          x = Math.random() * this.gameWidth;
          y = -50;
          break;
        case 1: // Right edge
          x = this.gameWidth + 50;
          y = Math.random() * this.gameHeight;
          break;
        case 2: // Bottom edge
          x = Math.random() * this.gameWidth;
          y = this.gameHeight + 50;
          break;
        case 3: // Left edge
          x = -50;
          y = Math.random() * this.gameHeight;
          break;
        default:
          x = -50;
          y = Math.random() * this.gameHeight;
      }
      
      attempts++;
    } while (
      attempts < 10 && 
      Math.abs(x - centerX) < safeBoundary && 
      Math.abs(y - centerY) < safeBoundary
    );
    
    const asteroid = new Asteroid(
      `large_asteroid_${index}_${Date.now()}`,
      x,
      y,
      AsteroidSize.LARGE,
      this.renderManager,
      this.audioManager,
      this.gameWidth,
      this.gameHeight
    );
    
    // Ensure asteroid has some velocity toward the play area
    const dx = centerX - x;
    const dy = centerY - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance > 0) {
      const normalizedDx = dx / distance;
      const normalizedDy = dy / distance;
      const speed = 20 + Math.random() * 30;
      
      // Mix directed movement with some randomness
      asteroid.velocity.x = normalizedDx * speed * 0.7 + (Math.random() - 0.5) * speed * 0.6;
      asteroid.velocity.y = normalizedDy * speed * 0.7 + (Math.random() - 0.5) * speed * 0.6;
    }
    
    this.asteroids.push(asteroid);
  }

  public getAsteroids(): Asteroid[] {
    return this.asteroids;
  }

  public addAsteroid(asteroid: Asteroid): void {
    this.asteroids.push(asteroid);
  }

  public removeAsteroid(asteroid: Asteroid): void {
    const index = this.asteroids.indexOf(asteroid);
    if (index !== -1) {
      this.asteroids.splice(index, 1);
    }
  }

  public getActiveAsteroidCount(): number {
    return this.asteroids.filter(asteroid => asteroid.active).length;
  }

  public getLargeAsteroidCount(): number {
    return this.asteroids.filter(asteroid => 
      asteroid.active && asteroid.getSize() === AsteroidSize.LARGE
    ).length;
  }

  public getMediumAsteroidCount(): number {
    return this.asteroids.filter(asteroid => 
      asteroid.active && asteroid.getSize() === AsteroidSize.MEDIUM
    ).length;
  }

  public getSmallAsteroidCount(): number {
    return this.asteroids.filter(asteroid => 
      asteroid.active && asteroid.getSize() === AsteroidSize.SMALL
    ).length;
  }

  public cleanup(): void {
    this.asteroids = [];
  }
}